import { useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import SignaturePad from 'signature_pad';
import { getSession } from '../components/ProtectedRoute';

const MESOS = ['gener','febrer','març','abril','maig','juny','juliol','agost','setembre','octubre','novembre','desembre'];
const VOCALS = ['abril','agost','octubre'];

function dataFormatCatala(date) {
  const d = date || new Date();
  const day = d.getDate();
  const mes = MESOS[d.getMonth()];
  const year = d.getFullYear();
  const prep = VOCALS.includes(mes) ? "d'" : 'de ';
  return `${day} ${prep}${mes} de ${year}`;
}

const EMPTY_FORM = {
  nom_juridic: '',
  nom_comercial: '',
  adreca: '',
  persona_contacte: '',
  carrec: '',
  telefon: '',
  telefon_whatsapp: '',
  email: '',
  canal_telefon: 0,
  canal_email: 0,
  canal_whatsapp: 0,
  autoritzacio_1: 0,
  autoritzacio_1_telefon: 0,
  autoritzacio_1_email: 0,
  autoritzacio_1_whatsapp: 0,
  autoritzacio_2: 0,
};

const REQUIRED_FIELDS = [
  { name: 'nom_juridic', label: 'Nom jurídic' },
  { name: 'nom_comercial', label: 'Nom comercial' },
  { name: 'adreca', label: "Adreça de l'activitat" },
  { name: 'persona_contacte', label: 'Persona de contacte' },
  { name: 'carrec', label: 'Càrrec / funció' },
  { name: 'email', label: 'Correu electrònic' },
];

export default function NouComerc() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});

  const [sigMode, setSigMode] = useState('dit');
  const [sigNom, setSigNom] = useState('');
  const canvasRef = useRef(null);
  const sigPadRef = useRef(null);

  const [showSpinner, setShowSpinner] = useState(false);
  const [toast, setToast] = useState(null);
  const [showValidacioModal, setShowValidacioModal] = useState(null);
  const sigBase64Ref = useRef(null);

  function setField(name, value) {
    setFormData(prev => {
      const next = { ...prev, [name]: value };
      if (name === 'autoritzacio_1' && !value) {
        next.autoritzacio_1_telefon = 0;
        next.autoritzacio_1_email = 0;
        next.autoritzacio_1_whatsapp = 0;
      }
      return next;
    });
    setErrors(prev => ({ ...prev, [name]: undefined }));
  }

  function handleCheckbox(name) {
    const val = formData[name] ? 0 : 1;
    setField(name, val);
  }

  // Canvas init with requestAnimationFrame
  const initCanvas = useCallback((node) => {
    canvasRef.current = node;
    if (!node) return;
    requestAnimationFrame(() => {
      const ratio = window.devicePixelRatio || 1;
      node.width = node.offsetWidth * ratio;
      node.height = node.offsetHeight * ratio;
      const ctx = node.getContext('2d');
      ctx.scale(ratio, ratio);
      sigPadRef.current = new SignaturePad(node, {
        backgroundColor: 'rgb(255,255,255)',
        penColor: 'rgb(0,0,0)',
      });
      sigPadRef.current.addEventListener('endStroke', () => {
        setErrors(prev => ({ ...prev, signatura: undefined }));
      });
    });
  }, []);

  async function getSignatureBase64() {
    if (sigMode === 'dit') {
      if (!sigPadRef.current || sigPadRef.current.isEmpty()) return null;
      return sigPadRef.current.toDataURL('image/png');
    }
    if (!sigNom.trim()) return null;
    await document.fonts.load('48px DancingScript');
    const c = document.createElement('canvas');
    c.width = 400; c.height = 150;
    const ctx = c.getContext('2d');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, 400, 150);
    ctx.font = '48px DancingScript';
    ctx.fillStyle = '#000';
    ctx.textBaseline = 'middle';
    ctx.fillText(sigNom.trim(), 20, 75);
    return c.toDataURL('image/png');
  }

  async function validate() {
    const errs = {};
    for (const { name, label } of REQUIRED_FIELDS) {
      if (!formData[name] || !String(formData[name]).trim()) {
        errs[name] = `"${label}" és obligatori`;
      }
    }
    if (formData.autoritzacio_1_telefon && (!formData.telefon || !formData.telefon.trim())) {
      errs.telefon = 'Obligatori si autoritzes contacte per telèfon';
    }
    if (formData.autoritzacio_1_whatsapp && (!formData.telefon_whatsapp || !formData.telefon_whatsapp.trim())) {
      errs.telefon_whatsapp = 'Obligatori si autoritzes contacte per WhatsApp';
    }
    if (!formData.autoritzacio_1 || !formData.autoritzacio_2) {
      errs.autoritzacions = 'Cal acceptar les dues autoritzacions per continuar.';
    }
    if (formData.autoritzacio_1 && !formData.autoritzacio_1_telefon && !formData.autoritzacio_1_email && !formData.autoritzacio_1_whatsapp) {
      errs.subcanals = 'Cal seleccionar almenys un canal de contacte.';
    }
    if (!await getSignatureBase64()) {
      errs.signatura = 'Cal signar el document per continuar.';
    }
    return errs;
  }

  async function handleEnviar() {
    const errs = await validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      setShowValidacioModal(Object.values(errs));
      return;
    }
    sigBase64Ref.current = await getSignatureBase64();
    setShowSpinner(true);
    try {
      const session = getSession();
      const body = {
        ...formData,
        firma_base64: sigBase64Ref.current,
        nom_enquestador: session?.enquestador_nom || '',
      };
      const res = await fetch('/api/comercos/nou', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      setShowSpinner(false);
      if (!res.ok) {
        setToast({ msg: 'Error en la generació del document.', color: '#DF273F' });
        setTimeout(() => setToast(null), 4000);
        return;
      }
      if (data.emailError) {
        setToast({ msg: "Document guardat. Error en l'enviament del correu.", color: '#d97706', accio: () => { setToast(null); navigate('/app'); } });
      } else {
        setToast({ msg: 'Document generat i enviat correctament', color: '#16a34a' });
        setTimeout(() => { setToast(null); navigate('/app'); }, 3000);
      }
    } catch {
      setShowSpinner(false);
      setToast({ msg: 'Error en la generació del document.', color: '#DF273F' });
      setTimeout(() => setToast(null), 4000);
    }
  }

  const today = dataFormatCatala(new Date());

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <header className="sticky top-0 z-10 bg-white border-b border-gray-200 flex items-center h-14 px-4 gap-3">
        <button
          onClick={() => navigate('/app')}
          className="flex items-center gap-1 text-sm font-medium min-h-[44px] px-2"
          style={{ color: '#444444' }}
        >
          <ArrowLeft size={16} /> Tornar
        </button>
        <h1 className="flex-1 text-center font-semibold text-dark truncate" style={{ color: '#444444' }}>
          Nou comerci
        </h1>
        <div className="w-20" />
      </header>

      <main className="flex-1 overflow-y-auto pb-24">
        <div className="max-w-2xl mx-auto px-4 py-6 space-y-8">

          {/* BLOC 1 — Dades de l'establiment */}
          <section>
            <h2 className="font-bold text-base mb-4" style={{ color: '#444444' }}>
              Dades de l'establiment
            </h2>
            <div className="space-y-3">
              {[
                { label: "Nom jurídic", name: "nom_juridic", type: "text", required: true },
                { label: "Nom comercial", name: "nom_comercial", type: "text", required: true },
                { label: "Adreça de l'activitat", name: "adreca", type: "text", required: true },
                { label: "Persona de contacte", name: "persona_contacte", type: "text", required: true },
                { label: "Càrrec / funció", name: "carrec", type: "text", required: true },
                { label: "Telèfon de l'activitat comercial", name: "telefon", type: "text", required: !!formData.autoritzacio_1_telefon },
                { label: "Telèfon WhatsApp", name: "telefon_whatsapp", type: "text", required: !!formData.autoritzacio_1_whatsapp },
                { label: "Correu electrònic", name: "email", type: "email", required: true },
              ].map(({ label, name, type, required }) => (
                <div key={name}>
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    {label}{required && <span className="text-red-500 ml-0.5">*</span>}
                  </label>
                  <input
                    type={type}
                    value={formData[name] ?? ''}
                    onChange={e => setField(name, e.target.value)}
                    className="w-full rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-red-200 min-h-[44px]"
                    style={{ border: '1px solid #DF273F' }}
                  />
                  {errors[name] && (
                    <p className="text-red-600 text-xs mt-1">{errors[name]}</p>
                  )}
                </div>
              ))}
            </div>
          </section>

          {/* BLOC 2 — Canal preferent */}
          <section>
            <h2 className="font-bold text-base mb-4" style={{ color: '#444444' }}>
              Canal preferent de contacte
            </h2>
            <div className="space-y-3">
              {[
                { label: "Telèfon", name: "canal_telefon" },
                { label: "Correu electrònic", name: "canal_email" },
                { label: "WhatsApp", name: "canal_whatsapp" },
              ].map(({ label, name }) => (
                <label key={name} className="flex items-center gap-3 cursor-pointer min-h-[44px]">
                  <input
                    type="checkbox"
                    checked={!!formData[name]}
                    onChange={() => handleCheckbox(name)}
                    className="w-5 h-5 rounded"
                    style={{ accentColor: '#DF273F' }}
                  />
                  <span className="text-sm text-gray-700">{label}</span>
                </label>
              ))}
            </div>
          </section>

          {/* BLOC 3 — Autoritzacions RGPD */}
          <section>
            <h2 className="font-bold text-base mb-3" style={{ color: '#444444' }}>
              Autoritzacions RGPD
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              Signant aquest document, autoritzo l'Ajuntament de Montcada i Reixac a tractar les dades facilitades amb les finalitats que s'indiquen a continuació:
            </p>

            <label className="flex items-start gap-3 cursor-pointer mb-2 min-h-[44px]">
              <input
                type="checkbox"
                checked={!!formData.autoritzacio_1}
                onChange={() => handleCheckbox('autoritzacio_1')}
                className="w-5 h-5 rounded mt-0.5 flex-shrink-0"
                style={{ accentColor: '#DF273F' }}
              />
              <span className="text-sm text-gray-700 font-medium">
                AUTORITZO el contacte per a comunicacions vinculades exclusivament al servei municipal de residus a través de:
              </span>
            </label>

            <div className="ml-8 space-y-2 mb-4">
              {[
                { label: "Trucada telefònica", name: "autoritzacio_1_telefon" },
                { label: "Correu electrònic", name: "autoritzacio_1_email" },
                { label: "WhatsApp", name: "autoritzacio_1_whatsapp" },
              ].map(({ label, name }) => (
                <label
                  key={name}
                  className={`flex items-center gap-3 cursor-pointer min-h-[44px] ${!formData.autoritzacio_1 ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={!!formData[name]}
                    onChange={() => formData.autoritzacio_1 && handleCheckbox(name)}
                    disabled={!formData.autoritzacio_1}
                    className="w-5 h-5 rounded"
                    style={{ accentColor: '#DF273F' }}
                  />
                  <span className="text-sm text-gray-700">{label}</span>
                </label>
              ))}
              {errors.subcanals && (
                <p className="text-red-600 text-sm mt-1">{errors.subcanals}</p>
              )}
            </div>

            <label className="flex items-start gap-3 cursor-pointer min-h-[44px]">
              <input
                type="checkbox"
                checked={!!formData.autoritzacio_2}
                onChange={() => handleCheckbox('autoritzacio_2')}
                className="w-5 h-5 rounded mt-0.5 flex-shrink-0"
                style={{ accentColor: '#DF273F' }}
              />
              <span className="text-sm text-gray-700 font-medium">
                AUTORITZO a utilitzar les meves dades per als serveis i recursos que Montcada Avança i l'Ajuntament de Montcada posa a la teva disposició.
              </span>
            </label>

            {errors.autoritzacions && (
              <p className="text-red-600 text-sm mt-2">{errors.autoritzacions}</p>
            )}
          </section>

          {/* BLOC 4 — Signatura */}
          <section>
            <style>{`
              @font-face {
                font-family: 'DancingScript';
                src: url('/assets/fonts/DancingScript.ttf') format('truetype');
                font-display: swap;
              }
            `}</style>
            <p className="font-bold text-base mb-3" style={{ color: '#444444' }}>
              Signatura (Nom i cognoms de la persona informada)
            </p>

            <div className="flex mb-4 rounded-xl overflow-hidden" style={{ border: '1px solid #DF273F' }}>
              {[
                { key: 'dit', label: 'Firma amb el dit' },
                { key: 'nom', label: 'Firma amb el nom' },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setSigMode(key)}
                  className="flex-1 text-sm font-medium transition-colors"
                  style={{
                    minHeight: '44px',
                    backgroundColor: sigMode === key ? '#DF273F' : '#fff',
                    color: sigMode === key ? '#fff' : '#DF273F',
                  }}
                >
                  {label}
                </button>
              ))}
            </div>

            {sigMode === 'dit' && (
              <div className="space-y-2">
                <canvas
                  ref={initCanvas}
                  className="w-full rounded-lg touch-none"
                  style={{ border: '1px solid #DF273F', background: '#fff', minHeight: '150px', height: '150px' }}
                />
                <button
                  type="button"
                  onClick={() => sigPadRef.current?.clear()}
                  className="text-sm font-medium px-4 rounded-lg"
                  style={{ minHeight: '44px', color: '#444444', border: '1px solid #444444' }}
                >
                  Esborrar
                </button>
              </div>
            )}

            {sigMode === 'nom' && (
              <div className="space-y-3">
                <input
                  type="text"
                  value={sigNom}
                  onChange={e => {
                    setSigNom(e.target.value);
                    setErrors(prev => ({ ...prev, signatura: undefined }));
                  }}
                  placeholder="Nom i cognoms"
                  className="w-full rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-red-200 min-h-[44px]"
                  style={{ border: '1px solid #DF273F' }}
                />
                <div
                  className="w-full rounded-lg px-4 flex items-center"
                  style={{
                    minHeight: '150px',
                    border: '1px solid #DF273F',
                    background: '#fff',
                    fontFamily: 'DancingScript, cursive',
                    fontSize: '2.5rem',
                    color: '#000',
                  }}
                >
                  {sigNom || <span className="text-gray-300" style={{ fontFamily: 'inherit' }}>Nom i cognoms</span>}
                </div>
              </div>
            )}

            {errors.signatura && (
              <p role="alert" className="text-red-600 text-sm mt-2">{errors.signatura}</p>
            )}

            <div className="mt-4 text-sm text-gray-500 space-y-1">
              <p>Montcada i Reixac</p>
              <p>{today}</p>
            </div>
          </section>

          {/* BLOC 5 — RGPD */}
          <section>
            <div className="text-gray-500 space-y-2" style={{ fontSize: '11px' }}>
              <p><strong>INFORMACIÓ SOBRE PROTECCIÓ DE DADES PERSONALS (RGPD 2016/679 i LOPDGDD 3/2018)</strong></p>
              <p><strong>RESPONSABLE DEL TRACTAMENT:</strong> Ajuntament de Montcada i Reixac (P0812400J).</p>
              <p><strong>FINALITATS DEL TRACTAMENT:</strong> Gestionar i tramitar el cens de comerços per a la recollida comercial, així com enviar informació d'interès comercial i dur a terme, si escau, la tramitació administrativa que se'n pugui derivar.</p>
              <p><strong>LEGITIMACIÓ PER AL TRACTAMENT:</strong> RGPD 679/2016 Art.6.1.e) El tractament és necessari per al compliment d'una missió realitzada en interès públic. RGPD 679/2016 Article 6.1.c) El tractament és necessari per al compliment d'una obligació legal aplicable al responsable del tractament. RGPD 679/2016 Article 6.1.b) El tractament és necessari per al compliment d'una relació contractual.</p>
              <p><strong>DESTINATARIS DE LES DADES:</strong> Es comunicarà les dades necessàries per a efectuar el pagament de les indemnitzacions i els que siguin necessaris per a complir amb una obligació legal.</p>
              <p><strong>TERMINI DE CONSERVACIÓ:</strong> Les dades proporcionades es conservaran durant el temps necessari per a complir amb les obligacions legals del Responsable.</p>
              <p><strong>EXERCICI DE DRETS:</strong> Les persones interessades podran sol·licitar l'accés, rectificació o supressió de les seves dades, així com exercir altres drets, adreçant-se al correu electrònic delegatpd@montcada.org o presencialment a l'Ajuntament a Av. de la Unitat 6, 08110 Montcada i Reixac.</p>
              <p><strong>CONTACTE DELEGAT DE PROTECCIÓ DE DADES I INFORMACIÓ ADDICIONAL:</strong> delegatpd@montcada.org</p>
            </div>
          </section>

        </div>
      </main>

      <footer className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-4 py-3 z-10">
        <div className="max-w-2xl mx-auto">
          <button
            onClick={handleEnviar}
            className="w-full rounded-xl text-white font-semibold text-base min-h-[48px]"
            style={{ backgroundColor: '#444444' }}
          >
            Generar PDF i enviar
          </button>
        </div>
      </footer>

      {/* Overlay spinner */}
      {showSpinner && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl px-8 py-6 flex flex-col items-center gap-4">
            <div className="w-8 h-8 border-4 border-gray-200 rounded-full animate-spin" style={{ borderTopColor: '#DF273F' }} />
            <p className="text-sm font-medium" style={{ color: '#444444' }}>Generant el document...</p>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 text-white px-6 py-3 rounded-xl shadow-lg text-sm font-medium flex items-center gap-4"
          style={{ backgroundColor: toast.color }}
        >
          <span>{toast.msg}</span>
          {toast.accio && (
            <button onClick={toast.accio} className="underline font-semibold whitespace-nowrap">
              D'acord
            </button>
          )}
        </div>
      )}

      {/* Modal — errors de validació */}
      {showValidacioModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 max-w-sm w-full">
            <h3 className="font-bold text-base mb-4" style={{ color: '#DF273F' }}>
              Cal completar el formulari
            </h3>
            <ul className="space-y-2 mb-6">
              {showValidacioModal.map((msg, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                  <span className="mt-0.5 flex-shrink-0 text-red-500">•</span>
                  {msg}
                </li>
              ))}
            </ul>
            <button
              onClick={() => setShowValidacioModal(null)}
              className="w-full rounded-xl text-white font-semibold text-sm min-h-[48px]"
              style={{ backgroundColor: '#444444' }}
            >
              D'acord
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
